import {
    AriaAttributes,
    ElementType,
    forwardRef,
    HTMLAttributeAnchorTarget,
    JSXElementConstructor,
    MouseEvent,
    ReactNode,
    useCallback,
} from 'react';
import { useButton, useHover, useLink } from 'react-aria';
import { DOMProps, FocusableElement, FocusableProps, HoverEvents, PressEvent } from '@react-types/shared';
import { genericMemo, JSONValue } from '@direct-frontend/stdlib';
import { Loggable, useLogger } from '@direct-frontend/modules-logger';

import { cn } from '../../lib/css/className/className';
import { useObjectRef } from '../../hooks/useObjectRef/useObjectRef';
import { useFocusOptions } from '../../hooks/useFocusOptions';
import { TestProps } from '../../internal/types/testTypes';
import { PressProps } from '../../internal/types/pressTypes';

import './ButtonBase.css';

export interface ButtonBaseProps extends TestProps, PressProps, FocusableProps, HoverEvents, DOMProps, Loggable {
    /**
     * Содержимое кнопки
     */
    content?: ReactNode;

    /**
     * Функция получения класса для кнопки
     */
    getClassName: ({
        isHovered,
        isPressed,
        isDisabled,
    }: {
        isHovered?: boolean;
        isPressed?: boolean;
        isDisabled?: boolean;
    }) => string;

    /**
     * Признак отключения кнопки
     */
    isDisabled?: boolean;

    /**
     * Обработчик нажатия на кнопку
     */
    onClick?: () => void;

    /**
     * Текст во всплывающей подсказке
     *
     * @notInFigma
     */
    title?: string;

    /**
     * Тип кнопки
     *
     * @notInFigma
     */
    type?: 'button' | 'submit' | 'reset';

    /**
     * Параметр участия в последовательной навигации
     */
    tabIndex?: number;

    /**
     * Альтернативный тег для компонента (например, 'div')
     * @default button
     */
    as?: ElementType | JSXElementConstructor<unknown>;

    /**
     * Аттрибуты WAI-ARIA 1.1 для использования с альтернативными тегами
     */
    ariaProps?: AriaAttributes;

    /**
     * HTML-атрибут href для кнопки, при его передаче кнопка становится ссылкой
     */
    href?: string;

    /**
     * Признак кнопки для скачивания.
     * Влияет на аттрибут download, чтобы не мигал экран при скачивании файлов
     */
    isDownload?: boolean;

    /**
     * HTML-атрибут target для ссылки
     */
    target?: HTMLAttributeAnchorTarget;

    /**
     * HTML-атрибут rel для ссылки
     */
    rel?: string;

    /**
     * Обработчик нажатия на ссылку с defaultPrevented
     * @deprecated
     */
    onClickNative?: (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;

    /**
     * Данные для логирования клика
     */
    logClickData?: Record<string, JSONValue>;
}

export const cls = cn('ButtonBase');

/**
 * Базовый компонент для создания кнопок
 *
 * Реализована базовая логика (состояние кнопок (hovered, focused, disabled), нажатие на кнопку, логирование)
 * Определены неспецифичные стили
 * @internal
 */
export const ButtonBase = genericMemo(
    forwardRef<HTMLButtonElement, ButtonBaseProps>(function ButtonBase(props, forwardedRef) {
        const {
            content,
            getClassName,
            isDisabled,
            title,
            onPress,
            onKeyUp: propsOnKeyUp,
            onKeyDown: propsOnKeyDown,
            tabIndex,
            href,
            isDownload,
            as: ElementTag = href ? 'a' : 'button',
            ariaProps,
            logNode,
            logClickData,
        } = props;
        // onClick — deprecated в react-aria, поэтому не передаем его в хук явно, а обрабатываем через
        // onClickNative и используем строго в тех местах, где нужен клик с preventedDefault
        const { onClick, onClickNative, ...restProps } = props;

        const { LogProvider, logClick } = useLogger('Button', logNode);
        const ref = useObjectRef(forwardedRef);

        const onClickHandler = useCallback(
            (event: PressEvent) => {
                logClick(logClickData);

                if (onPress) {
                    onPress(event);
                } else {
                    onClick?.();
                }
            },
            [logClick, logClickData, onClick, onPress],
        );

        /**
         * Получаем только обработчик нажатия на ссылку для роутерных переходов (если указан href)
         */
        const linkRef = useObjectRef<FocusableElement>(null);
        const {
            linkProps: { onClick: onClickLink },
        } = useLink({ href }, linkRef);

        const onClickNativeHandler = useCallback(
            (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
                onClickNative?.(event);
                onClickLink?.(event);
            },
            [onClickNative, onClickLink],
        );

        const { buttonProps, isPressed } = useButton(
            {
                ...restProps,
                onPress: onClickHandler,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
                // @ts-expect-error onClick - deprecated-свойство и отсутствует в типах, но на самом деле принимается
                onClick: onClickNative ? onClickNativeHandler : undefined,
                elementType: ElementTag,
            },
            ref,
        );

        const { hoverProps, isHovered } = useHover({ ...props, isDisabled: false });
        // TODO DIRECT-263743 Поресерчить фокусы в модалке на мобиле, после этого если нужно - откатить
        const { focusProps, isFocusVisible } = useFocusOptions();

        // перемещение интерактивных props в отдельный объект
        const {
            onClick: onButtonClick,
            onKeyDown,
            onKeyUp,
            onMouseDown,
            onPointerDown,
            onPointerUp,
            ...otherButtonProps
        } = buttonProps;

        const interactiveProps = {
            /*
             * При отсутствии onClickNative в кнопках-ссылках не используем обработчик onClick
             * из useButton, чтобы не триггерить warning с сообщением о deprecated onClick
             */
            onClick: href ? onClickNativeHandler : onButtonClick,
            onKeyDown,
            onKeyUp,
            onMouseDown,
            onPointerDown,
            onPointerUp,
        };

        const className = getClassName({ isHovered, isPressed, isDisabled });

        return (
            <LogProvider>
                <ElementTag
                    // выборочная передача interactiveProps при использовании альтернативных тегов
                    // позволяет не перезаписывать события по умолчанию (например, для summary)
                    {...(onClickNative
                        ? {
                              onClick: onButtonClick,
                              onKeyUp: propsOnKeyUp,
                              onKeyDown: propsOnKeyDown,
                          }
                        : (onClick || onPress || href) && interactiveProps)}
                    {...otherButtonProps}
                    {...hoverProps}
                    {...focusProps}
                    {...ariaProps}
                    ref={ref}
                    download={isDownload}
                    title={title}
                    tabIndex={tabIndex}
                    data-testid={props['data-testid'] || 'Button'}
                    className={cls(
                        {
                            pressed: isPressed,
                            disabled: isDisabled,
                            focused: isFocusVisible,
                        },
                        [className],
                    )}
                >
                    {content}
                </ElementTag>
            </LogProvider>
        );
    }),
);
